import { apiFetch } from '@/lib/api-client';

/**
 * API client for the Batch 9A backend subscription endpoints
 * (`/api/subscriptions/portal-session` + `/api/subscriptions/reactivate`).
 *
 * `/api/subscriptions/me` is consumed via `useSubscriptionState()` —
 * this module is for the action endpoints only. State reads belong in
 * the hook so cache + refetch semantics stay in one place.
 */

export type PortalFlow = 'subscription_update' | 'subscription_cancel' | 'payment_method_update';

/**
 * Create a Stripe Customer Portal session and return the redirect URL.
 *
 * Caller redirects with `window.location.href = url`. Sessions are
 * short-lived (Stripe defaults ~5 min); never cache the returned URL.
 *
 * `flow` (optional) deep-links the session to a Portal sub-flow:
 *   - undefined                → generic portal home ("Abo verwalten")
 *   - 'subscription_update'    → plan-update screen ("Plan-Wechsel
 *                                rückgängig machen")
 *   - 'subscription_cancel'    → cancel-flow screen ("Kündigung
 *                                fortsetzen" primary CTA)
 *   - 'payment_method_update'  → payment-method screen ("Zahlungs-
 *                                methode aktualisieren")
 *
 * Backend rejects (400) when `subscription_update` /
 * `subscription_cancel` are requested without an active subscription.
 */
export function createPortalSession(flow?: PortalFlow): Promise<{ url: string }> {
  return apiFetch<{ url: string }>('/api/subscriptions/portal-session', {
    method: 'POST',
    body: flow ? { flow } : {},
  });
}

/**
 * Reactivate a pending-cancel subscription. Backend calls
 * `stripe.subscriptions.update(... { cancel_at_period_end: false })`
 * directly — first backend-initiated Stripe state mutation in this
 * codebase.
 *
 * Returns 204 (no content). PWA pattern: call → wait ~500ms for the
 * `customer.subscription.updated` webhook to sync DB → call
 * `useSubscriptionState().refetch()` to pull the synced state into the
 * UI.
 *
 * Throws via `apiFetch` on 4xx (400 when no active sub or sub is
 * not in pending-cancel state) / 5xx.
 */
export function reactivateSubscription(): Promise<void> {
  return apiFetch<void>('/api/subscriptions/reactivate', {
    method: 'POST',
  });
}

/**
 * Discard a scheduled plan change (D1) — the in-app "Plan-Wechsel rückgängig
 * machen" action. Backend (C2) releases the Stripe subscription schedule,
 * keeping the current plan running.
 *
 * Returns 204 (no content). Same PWA pattern as `reactivateSubscription`:
 * call → wait ~500ms for the `subscription_schedule.released` webhook (B4) to
 * sync DB → `useSubscriptionState().refetch()`.
 *
 * Throws via `apiFetch`. The caller treats two 4xx codes as success-equivalent
 * (the scheduled change is already gone): 404 `subscription_no_scheduled_change`
 * and 409 `subscription_schedule_already_terminated`. Other ApiError (5xx,
 * validation_error, …) / NetworkError surface the failure banner.
 */
export function discardScheduledChange(): Promise<void> {
  return apiFetch<void>('/api/subscriptions/discard-scheduled-change', {
    method: 'POST',
  });
}

/**
 * Atomic withdrawal-consent capture + Stripe Checkout Session creation
 * (Batch 11 Commit 5 — D-09 Bestellübersicht primary CTA).
 *
 * Backend mode-switches `subscription` vs `payment` based on the
 * `planKey` resolved against `PLAN_CATALOG`; one endpoint covers both
 * subscription and pack purchases. The DTO validates `consentGiven`
 * with `@Equals(true)` so a request with `false` is rejected with 400
 * before the service runs (§356 BGB hard gate).
 *
 * On success: returns `{ checkoutUrl }`. PWA caller does
 * `window.location.href = checkoutUrl` immediately — the URL is a
 * Stripe-hosted checkout page; PWA leaves the SPA at this point.
 *
 * Throws via `apiFetch` on 4xx (unknown planKey, consent missing) /
 * 5xx (Stripe API down, etc.).
 */
export function createConsentAndCheckout(
  planKey: string,
  consentGiven: boolean,
): Promise<{ checkoutUrl: string; consentId: string }> {
  return apiFetch<{ checkoutUrl: string; consentId: string }>(
    '/api/subscriptions/create-consent-and-checkout',
    {
      method: 'POST',
      body: { planKey, consentGiven },
    },
  );
}

// ── §356a BGB Widerrufs-Flow (L.3) ──────────────────────────────────

/** One withdrawable contract from GET /api/subscriptions/withdrawable. */
export interface WithdrawableContract {
  contractType: 'subscription' | 'package';
  contractId: string;
  contractIdentifier: string;
  label: string;
  canWithdraw: boolean;
  withdrawalDeadline: string | null;
}

/**
 * Unified list of the user's withdrawable contracts (subs + packages).
 * Backend returns an empty array when FEATURE_WITHDRAWAL_BUTTON_ENABLED is off.
 */
export function getWithdrawableContracts(): Promise<WithdrawableContract[]> {
  return apiFetch<WithdrawableContract[]>('/api/subscriptions/withdrawable');
}

/**
 * Record a confirmed §356a withdrawal. The backend re-checks eligibility and
 * returns 409 `withdrawal_not_eligible` if the contract can no longer be
 * withdrawn (handled by the caller via ApiError.errorCode).
 */
export function initiateWithdrawal(input: {
  contractType: 'subscription' | 'package';
  contractId: string;
  customerName: string;
  contractIdentifier: string;
  /** §356a Abs.2 — consumer-chosen channel for the Eingangsbestätigung. */
  contactEmail?: string;
}): Promise<{ ok: true; withdrawalRequestId: string; receivedAt: string }> {
  return apiFetch('/api/subscriptions/initiate-withdrawal', {
    method: 'POST',
    body: { ...input, confirm: true },
  });
}
